// A backrest (the Backrest tool): horizontal boards standing off a wall on
// spacers, over the high tier's seat. Only its figures are stored on the room
// (`ProductInstance.backrests`); where it stands is derived here from its host —
// a room wall, the room-facing face of a hidden heater wall, or one face of a
// partition — and its bench, so it follows the room, its benches and its heater
// walls. Two meeting at a corner join as benches do: at a sharp one one runs
// through and the other butts it, round a rounded one a corner piece. Pure: the
// plan, the sections, the RCP view, 3D and the lights behind it all read it.

import type {
  Backrest,
  BackrestCut,
  BackrestHost,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  BENCH_TIER_COLORS,
  STEAM_BENCH_TIER_COLORS,
} from './benches';
import { wallThicknessOf } from './dent';
import {
  arcPoints,
  pointInPolygon,
} from './geometry';
import { benchHeight } from './heights';
import {
  faceRadius,
  faceStation,
  innerWallDoorSpans,
  innerWallGlassSpans,
  innerWallSpan,
  isPolyRoom,
  outerDoorSpans,
  polyBenchBands,
  polyBenchEdges,
  polyBenchLifts,
  polyEdges,
  polyInnerBenchBands,
  RADIUS_EPS,
  type RoomCorner,
  roomCorners,
  wallFeatures,
  wallView,
} from './polyRoom';
import {
  contentMemo,
  HEAVY_FIELDS,
} from './plainData';

/** The figures a backrest takes when none is stored. */
export const BACKREST_DEFAULTS = {
  boards: 2,
  boardHeight: 82,
  boardThickness: 28,
  gap: 6,
  overSeat: 172,
  standOff: 28,
} as const;

/** A spacer's width along the run (mm) and the most two stand apart. */
export const BACKREST_SPACER = 40;
export const BACKREST_SPACER_GAP = 600;
/** A spacer stops short of the boards' edges, as cavalletto.dxf draws it: this
 *  far over the bottom board's foot and under the top board's top, where the up
 *  light sits on it (mm). */
export const BACKREST_SPACER_FOOT = 8;
export const BACKREST_SPACER_HEAD = 20;

/** The spacers' height band behind boards from `z0` to `z1`: short of both
 *  edges, or the boards' whole height where they are too short for that. */
export function backrestSpacerBand(z0: number, z1: number): [number, number] {
  const a = z0 + BACKREST_SPACER_FOOT, b = z1 - BACKREST_SPACER_HEAD;
  return b - a > 1 ? [a, b] : [z0, z1];
}
/** The shortest a backrest may be (mm). */
export const BACKREST_MIN_LEN = 100;
/** The seat a backrest stands over when no bench is on its host or in its room. */
export const BACKREST_SEAT_FALLBACK = 1050;
/** How far in front of a face the Backrest tool still takes it (mm). */
export const BACKREST_REACH = 450;

export type BackrestFigures = { [K in keyof typeof BACKREST_DEFAULTS]: number };

/** Every figure of a backrest, its own or the default. */
export function backrestFigures(br: Backrest): BackrestFigures {
  const pos = (v: number | undefined, d: number, min = 0): number => (typeof v === 'number' && Number.isFinite(v) && v >= min ? v : d);
  return {
    boards: Math.max(1, Math.round(pos(br.boards, BACKREST_DEFAULTS.boards, 1))),
    boardHeight: pos(br.boardHeight, BACKREST_DEFAULTS.boardHeight, 1),
    boardThickness: pos(br.boardThickness, BACKREST_DEFAULTS.boardThickness, 1),
    gap: pos(br.gap, BACKREST_DEFAULTS.gap),
    overSeat: pos(br.overSeat, BACKREST_DEFAULTS.overSeat),
    standOff: pos(br.standOff, BACKREST_DEFAULTS.standOff),
  };
}

export function backrestsOf(inst: ProductInstance): Backrest[] {
  return inst.backrests ?? [];
}

/**
 * A face a backrest stands against, in the room's local frame: station `s`
 * along it is the point `o + u·s`, and `n` points from the face into the room.
 * `[lo, hi]` is its usable stretch. A wall's stations are its outer edge's (mm
 * from its first corner, as a bench's `start` / `end`), its face the one the
 * benches stand against (`polyBenchEdges`: a heater wall's, where one stands); a
 * partition's run inward from the host wall's inner face along its length.
 */
export interface BackrestFace { o: Vec2; u: Vec2; n: Vec2; lo: number; hi: number }

/** The wall a host stands on, or the partition and its face. */
function hostWall(inst: ProductInstance, host: BackrestHost): number | null {
  if ('wall' in host) return host.wall;
  if ('heaterWall' in host) return inst.heaterWalls?.find((h) => h.id === host.heaterWall)?.edge ?? null;
  return null;
}

/** The face a host gives, or null when it no longer resolves (a wall gone, a
 *  heater wall or a partition deleted). A wall's runs corner to corner. */
export function backrestFace(inst: ProductInstance, host: BackrestHost): BackrestFace | null {
  if (!isPolyRoom(inst)) return null;
  const view = wallView(inst);
  if ('partition' in host) {
    const iw = view.innerWalls?.find((w) => w.id === host.partition);
    const e = iw ? polyEdges(view)[iw.edge ?? -1] : undefined;
    const sp = iw ? innerWallSpan(view, iw) : null;
    if (!iw || !e || !sp || !(iw.length > 0)) return null;
    const s = host.face === 'a' ? sp.s : sp.t;
    const T = wallThicknessOf(view);
    const o = { x: e.a.x + e.dir.x * s + e.normal.x * T, y: e.a.y + e.dir.y * s + e.normal.y * T };
    const n = host.face === 'a' ? { x: -e.dir.x, y: -e.dir.y } : { x: e.dir.x, y: e.dir.y };
    return { o, u: e.normal, n, lo: 0, hi: iw.length };
  }
  const w = hostWall(view, host);
  const e = w == null ? undefined : polyBenchEdges(view)[w];
  if (!e) return null;
  const along = (p: Vec2): number => (p.x - e.a.x) * e.dir.x + (p.y - e.a.y) * e.dir.y;
  const lo = along(e.ia), hi = along(e.ib);
  if (!(hi - lo > 1)) return null;
  // The face line, its station 0 opposite the wall's first corner.
  const o = { x: e.ia.x - e.dir.x * lo, y: e.ia.y - e.dir.y * lo };
  return { o, u: e.dir, n: e.normal, lo, hi };
}

/** A line in the room's plan: a point on it and its direction. */
export interface BackrestLine { p: Vec2; dir: Vec2 }

/** One end of a backrest as built: its station along the face, and the line its
 *  boards are cut along — the wall it runs into, or the backrest it butts —, or
 *  square (null). */
export interface BackrestEnd { s: number; cut: BackrestLine | null }

/** Where a backrest runs along its face. `s0` / `s1` are its span as stored,
 *  else over its bench (to the corner where that bench meets another), else the
 *  whole face — what the panel and the grips show and set; `face.lo` / `hi` the
 *  most it may take (a rounded corner's curve left out). `ends` are its ends as
 *  built, the corners resolved, and `spacer` the stretch its spacers stand on. */
export interface BackrestRun {
  face: BackrestFace;
  s0: number;
  s1: number;
  ends: [BackrestEnd, BackrestEnd];
  spacer: [number, number];
  /** The span it takes with no stored ends: over its bench, else the whole face. */
  auto: [number, number];
  /** The bench it stands over, if any. */
  bench?: string;
}

/** A piece of boards filling a rounded corner between two matching backrests
 *  (never stored). `path(d)` is the line `d` mm off the walls round it, from the
 *  backrest on the wall before the corner to the one after; `owner` (that one)
 *  is the backrest it is drawn, picked and built with. */
export interface BackrestCorner {
  id: string;
  owner: string;
  other: string;
  vertex: number;
  boards: Vec2[];
  spacers: Vec2[][];
  path: (d: number) => Vec2[];
}

/** Every backrest of a room resolved: each one's run and the corner pieces. */
export interface BackrestLayout { runs: Map<string, BackrestRun>; corners: BackrestCorner[] }

const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Where the lines through `a` along `u` and through `b` along `v` cross. */
function cross(a: Vec2, u: Vec2, b: Vec2, v: Vec2): Vec2 | null {
  const den = u.x * v.y - u.y * v.x;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((b.x - a.x) * v.y - (b.y - a.y) * v.x) / den;
  return { x: a.x + u.x * t, y: a.y + u.y * t };
}

/** The station at `d` mm off the face where an end's boards stop: on its cut
 *  line, else square at its station. */
export function endStation(face: BackrestFace, end: BackrestEnd, d: number): number {
  if (!end.cut) return end.s;
  const x = cross(facePoint(face, 0, d), face.u, end.cut.p, end.cut.dir);
  return x ? dot(sub(x, face.o), face.u) : end.s;
}

/** A run's plan quad between depths `d0` and `d1` off its face, its ends cut. */
export function runQuad(run: BackrestRun, d0: number, d1: number): Vec2[] {
  const { face, ends } = run;
  return [
    facePoint(face, endStation(face, ends[0], d0), d0), facePoint(face, endStation(face, ends[1], d0), d0),
    facePoint(face, endStation(face, ends[1], d1), d1), facePoint(face, endStation(face, ends[0], d1), d1),
  ];
}

/** A bench a backrest can stand over: its id, seat, span along the face and plan. */
interface FaceBench { id: string; seat: number; s0: number; s1: number; poly: Vec2[] }

/** The benches standing against a host's face (their seats with a stepped
 *  profile's lift), their spans in the face's stations. */
function faceBenches(inst: ProductInstance, host: BackrestHost, face: BackrestFace): FaceBench[] {
  const src = { benchTierHeights: inst.benchTierHeights };
  const view = wallView(inst);
  if ('partition' in host) {
    return polyInnerBenchBands(inst)
      .filter((b) => view.innerWalls?.[b.iwIndex]?.id === host.partition && b.face === host.face)
      .map((b) => ({ id: b.id, seat: benchHeight({ tier: b.level, height: b.leg.height }, src) + b.lift, s0: face.lo, s1: face.hi, poly: b.poly }));
  }
  const w = hostWall(view, host);
  const lifts = polyBenchLifts(inst);
  return polyBenchBands(inst)
    .filter((b) => b.edge === w)
    .map((b) => ({ id: b.id, seat: benchHeight({ tier: b.level, height: inst.polyBenches?.find((x) => x.id === b.id)?.height }, src) + (lifts.get(b.id) ?? 0), s0: b.start, s1: b.end, poly: b.poly }));
}

/** The bench a backrest stands over: its own, else the highest on its face (the
 *  longer of two as high), else none. */
function chosenBench(benches: FaceBench[], br: Backrest): FaceBench | null {
  const own = br.bench ? benches.find((b) => b.id === br.bench) : undefined;
  if (own) return own;
  return benches.reduce<FaceBench | null>((m, b) => (!m || b.seat > m.seat + 0.5 || (Math.abs(b.seat - m.seat) <= 0.5 && b.s1 - b.s0 > m.s1 - m.s0) ? b : m), null);
}

/** The bench the Backrest tool puts a backrest over on `host` at `p` (room
 *  local): the highest one whose span the point is over, else the highest on the
 *  face; undefined where none stands there. */
export function backrestBenchAt(inst: ProductInstance, host: BackrestHost, p: Vec2): string | undefined {
  const face = backrestFace(inst, host);
  if (!face) return undefined;
  const s = dot(sub(p, face.o), face.u);
  const benches = faceBenches(inst, host, face);
  const over = benches.filter((b) => s >= Math.min(b.s0, b.s1) - 1 && s <= Math.max(b.s0, b.s1) + 1);
  return (chosenBench(over.length ? over : benches, { id: '', host }) ?? undefined)?.id || undefined;
}

/** The bench a backrest stands over, as resolved (its own, else the highest on
 *  its face) — the one the Backrest tool tells two backrests on a face apart by. */
export function backrestBenchOf(inst: ProductInstance, br: Backrest): string | undefined {
  const face = backrestFace(inst, br.host);
  return face ? chosenBench(faceBenches(inst, br.host, face), br)?.id || undefined : undefined;
}

/** The seats of a room's benches (their tops, a stepped profile's lift
 *  included). */
function roomSeats(inst: ProductInstance): number[] {
  const src = { benchTierHeights: inst.benchTierHeights };
  const lifts = polyBenchLifts(inst);
  return [
    ...polyBenchBands(inst).map((b) => benchHeight({ tier: b.level, height: inst.polyBenches?.find((x) => x.id === b.id)?.height }, src) + (lifts.get(b.id) ?? 0)),
    ...polyInnerBenchBands(inst).map((b) => benchHeight({ tier: b.level, height: b.leg.height }, src) + b.lift),
  ];
}

/**
 * The seat a backrest stands over: its bench's (its own, else the highest on its
 * wall, heater wall or partition face), else the highest in the room, else
 * {@link BACKREST_SEAT_FALLBACK}.
 */
export function backrestSeat(inst: ProductInstance, br: Backrest): number {
  const face = backrestFace(inst, br.host);
  const bench = face ? chosenBench(faceBenches(inst, br.host, face), br) : null;
  if (bench) return bench.seat;
  const seats = roomSeats(inst);
  return seats.length ? Math.max(...seats) : BACKREST_SEAT_FALLBACK;
}

/** Each board's height band (mm above the room floor), the lowest first. */
export function backrestBoards(inst: ProductInstance, br: Backrest): Array<{ z0: number; z1: number }> {
  return boardsOver(backrestSeat(inst, br), backrestFigures(br));
}

function boardsOver(seat: number, f: BackrestFigures): Array<{ z0: number; z1: number }> {
  const base = seat + f.overSeat;
  return Array.from({ length: f.boards }, (_, i) => {
    const z0 = base + i * (f.boardHeight + f.gap);
    return { z0, z1: z0 + f.boardHeight };
  });
}

/** A backrest's whole height band: under its lowest board to over its top one. */
export function backrestSpan(inst: ProductInstance, br: Backrest): { z0: number; z1: number } {
  const boards = backrestBoards(inst, br);
  return { z0: boards[0].z0, z1: boards[boards.length - 1].z1 };
}

/** The point at station `s`, `d` mm off the face. */
export function facePoint(face: BackrestFace, s: number, d: number): Vec2 {
  return { x: face.o.x + face.u.x * s + face.n.x * d, y: face.o.y + face.u.y * s + face.n.y * d };
}

/** The plan quad between depths `d0` and `d1` off the face over `[s0, s1]`. */
export function faceQuad(face: BackrestFace, s0: number, s1: number, d0: number, d1: number): Vec2[] {
  return [facePoint(face, s0, d0), facePoint(face, s1, d0), facePoint(face, s1, d1), facePoint(face, s0, d1)];
}

/** A backrest as the layout reads it before its corners are resolved. */
interface Placed {
  br: Backrest;
  idx: number;
  face: BackrestFace;
  f: BackrestFigures;
  /** Its wall (a wall's or a heater wall's), else null on a partition. */
  wall: number | null;
  /** How far its face is in from its wall's outer edge (mm). */
  inset: number;
  bench: FaceBench | null;
  seat: number;
  boards: Array<{ z0: number; z1: number }>;
  s0: number;
  s1: number;
  auto: [number, number];
  /** The rounded corner at either end its boards would curve round, if any. */
  round: [RoomCorner | null, RoomCorner | null];
  /** The straight face at the wall (spacers stand within it). */
  flat: [number, number];
  ends: [BackrestEnd, BackrestEnd];
  done: [boolean, boolean];
}

/**
 * Every backrest of a room resolved (and `extra`, one not yet stored — the
 * Backrest tool's preview — with them). Each runs over its span; where two meet
 * at a sharp corner, or where a wall's meets a partition's, they join as benches
 * do: the one whose bench runs through the corner runs through (else the one over
 * the higher seat, then on the longer face) to the other's face — on past it to
 * its front at an outside corner —, the other butting its boards; round a rounded
 * corner, two of the same boards join with a corner piece, else each stops where
 * its wall starts to curve. A lone one at a sharp corner is cut along the wall it
 * runs into.
 */
export function backrestLayout(inst: ProductInstance, extra?: Backrest): BackrestLayout {
  if (extra && inst.backrests?.includes(extra)) extra = undefined;
  if (!inst.backrests?.length && !extra) return { runs: new Map(), corners: [] };
  // Memoised on everything of the room it reads (its lights aside): the plan,
  // the strips and the hit tests all ask for it, many times a frame.
  return LAYOUTS.get(inst.id, () => [inst, extra], () => layoutOf(inst, extra));
}

const LAYOUTS = contentMemo<BackrestLayout>({ skip: new Set([...HEAVY_FIELDS, 'lights']) });

function layoutOf(inst: ProductInstance, extra?: Backrest): BackrestLayout {
  const out: BackrestLayout = { runs: new Map(), corners: [] };
  if (!isPolyRoom(inst)) return out;
  const list = backrestsOf(inst);
  const all = extra && !list.includes(extra) ? [...list, extra] : list;
  if (!all.length) return out;
  const view = wallView(inst);
  const edges = polyBenchEdges(view), n = edges.length;
  const corners = roomCorners(view);
  const roomSeat = ((): number => { const s = roomSeats(inst); return s.length ? Math.max(...s) : BACKREST_SEAT_FALLBACK; })();
  const placed: Placed[] = [];
  all.forEach((br, idx) => {
    const raw = backrestFace(inst, br.host);
    if (!raw) return;
    const f = backrestFigures(br);
    const wall = 'partition' in br.host ? null : hostWall(view, br.host);
    const face = { ...raw };
    let inset = 0, round: Placed['round'] = [null, null];
    const flat: [number, number] = [raw.lo, raw.hi];
    const e = wall == null ? undefined : edges[wall];
    if (e) {
      inset = dot(sub(e.ia, e.a), e.normal);
      const curves = (c: RoomCorner | null, d: number): c is RoomCorner => !!c && faceRadius(c, inset + d) > RADIUS_EPS;
      const c0 = corners[wall!] ?? null, c1 = corners[(wall! + 1) % n] ?? null;
      if (curves(c0, f.standOff)) { round = [c0, round[1]]; face.lo = c0.station; }
      if (curves(c1, f.standOff)) { round = [round[0], c1]; face.hi = e.len - c1.station; }
      if (curves(c0, 0)) flat[0] = c0.station;
      if (curves(c1, 0)) flat[1] = e.len - c1.station;
    }
    if (!(face.hi - face.lo > 1)) return;
    const bench = chosenBench(faceBenches(inst, br.host, raw), br);
    const clamp = (v: number): number => Math.min(face.hi, Math.max(face.lo, v));
    const ok = (v: number | undefined): v is number => typeof v === 'number' && Number.isFinite(v);
    const auto: [number, number] = bench ? [clamp(Math.min(bench.s0, bench.s1)), clamp(Math.max(bench.s0, bench.s1))] : [face.lo, face.hi];
    let s0: number, s1: number;
    if (ok(br.start) || ok(br.end)) { s0 = clamp(ok(br.start) ? br.start : face.lo); s1 = clamp(ok(br.end) ? br.end : face.hi); }
    else [s0, s1] = auto;
    if (s0 > s1) [s0, s1] = [s1, s0];
    if (s1 - s0 < 1) return;
    const seat = bench ? bench.seat : roomSeat;
    placed.push({
      br, idx, face, f, wall, inset, bench, seat, boards: boardsOver(seat, f), s0, s1, auto, round, flat,
      ends: [{ s: s0, cut: null }, { s: s1, cut: null }], done: [false, false],
    });
  });
  const atEnd = (P: Placed, k: 0 | 1): boolean => (k ? P.s1 >= P.face.hi - 1 : P.s0 <= P.face.lo + 1);

  // Rounded corners: a corner piece where the backrests either side meet with the
  // same boards, else each stops where its wall starts to curve.
  const same = (A: Placed, B: Placed): boolean => Math.abs(A.inset - B.inset) < 0.5
    && Math.abs(A.f.standOff - B.f.standOff) < 0.5 && Math.abs(A.f.boardThickness - B.f.boardThickness) < 0.5
    && A.boards.length === B.boards.length && A.boards.every((b, k) => Math.abs(b.z0 - B.boards[k].z0) < 0.5 && Math.abs(b.z1 - B.boards[k].z1) < 0.5);
  for (const A of placed) {
    const c = A.round[1];
    if (!c || A.wall == null || !atEnd(A, 1)) continue;
    const B = placed.find((P) => P.wall === (A.wall! + 1) % n && P.round[0] === c && atEnd(P, 0) && !P.done[0]);
    if (!B || !same(A, B)) continue;
    const { standOff: so, boardThickness: t } = A.f;
    const S = Math.max(faceStation(c, A.inset + so), faceStation(c, A.inset + so + t));
    const ep = polyEdges(view)[A.wall], ei = polyEdges(view)[B.wall!];
    A.ends[1] = { s: ep.len - S, cut: null }; B.ends[0] = { s: S, cut: null };
    A.done[1] = B.done[0] = true;
    const at = (e: typeof ep, s: number, d: number): Vec2 => ({ x: e.a.x + e.dir.x * s + e.normal.x * (A.inset + d), y: e.a.y + e.dir.y * s + e.normal.y * (A.inset + d) });
    const path = (d: number): Vec2[] => {
      const r = faceRadius(c, A.inset + d);
      const a0 = at(ep, ep.len - S, d), a1 = at(ei, S, d);
      const mid = r > RADIUS_EPS
        ? arcPoints(c.center, at(ep, ep.len - c.station, d), at(ei, c.station, d), r)
        : [cross(at(ep, 0, d), ep.dir, at(ei, 0, d), ei.dir) ?? a0];
      return [a0, ...mid, a1].filter((p, k, arr) => k === 0 || dist(p, arr[k - 1]) > 0.01);
    };
    const spacers: Vec2[][] = [];
    if (so > 0.5) {
      const midPath = path(so / 2);
      let L = 0;
      for (let k = 1; k < midPath.length; k++) L += dist(midPath[k - 1], midPath[k]);
      const count = Math.max(0, Math.ceil((L + BACKREST_SPACER) / BACKREST_SPACER_GAP) - 1);
      const r0 = faceRadius(c, A.inset), r1 = faceRadius(c, A.inset + so), rm = (r0 + r1) / 2;
      const from = at(ep, ep.len - c.station, 0), to = at(ei, c.station, 0);
      const a0 = Math.atan2(from.y - c.center.y, from.x - c.center.x);
      let sweep = Math.atan2(to.y - c.center.y, to.x - c.center.x) - a0;
      while (sweep <= -Math.PI) sweep += 2 * Math.PI;
      while (sweep > Math.PI) sweep -= 2 * Math.PI;
      const half = rm > 0 ? BACKREST_SPACER / 2 / rm : 0;
      const pt = (r: number, a: number): Vec2 => ({ x: c.center.x + r * Math.cos(a), y: c.center.y + r * Math.sin(a) });
      for (let j = 1; j <= count; j++) {
        const a = a0 + (sweep * j) / (count + 1);
        spacers.push([pt(r0, a - half), pt(r0, a + half), pt(r1, a + half), pt(r1, a - half)]);
      }
    }
    out.corners.push({ id: `brc-${c.v}`, owner: B.br.id, other: A.br.id, vertex: c.v, boards: [...path(so), ...path(so + t).reverse()], spacers, path });
  }
  for (const P of placed) {
    for (const k of [0, 1] as const) if (P.round[k] && atEnd(P, k)) P.done[k] = true;
  }

  // Sharp corners and partitions: ends whose feet meet.
  const footOf = (P: Placed, k: 0 | 1): Vec2 => facePoint(P.face, k ? P.s1 : P.s0, 0);
  const reaches = (X: Placed, Y: Placed, C: Vec2): boolean => !!X.bench
    && X.bench.poly.some((v) => dist(v, C) < 2500 && Math.abs(dot(sub(v, Y.face.o), Y.face.n)) < 2);
  const winner = (A: Placed, B: Placed, C: Vec2): [Placed, Placed] => {
    const ra = reaches(A, B, C), rb = reaches(B, A, C);
    if (ra !== rb) return ra ? [A, B] : [B, A];
    if (Math.abs(A.seat - B.seat) > 0.5) return A.seat > B.seat ? [A, B] : [B, A];
    const la = A.face.hi - A.face.lo, lb = B.face.hi - B.face.lo;
    if (Math.abs(la - lb) > 1) return la > lb ? [A, B] : [B, A];
    const wa = A.wall ?? Infinity, wb = B.wall ?? Infinity;
    if (wa !== wb) return wa < wb ? [A, B] : [B, A];
    return A.idx < B.idx ? [A, B] : [B, A];
  };
  const lineAt = (P: Placed, d: number): BackrestLine => ({ p: facePoint(P.face, 0, d), dir: P.face.u });
  for (let i = 0; i < placed.length; i++) {
    for (const ka of [0, 1] as const) {
      const A = placed[i];
      if (A.done[ka]) continue;
      const C = footOf(A, ka);
      for (let j = 0; j < placed.length && !A.done[ka]; j++) {
        const B = placed[j];
        if (j === i || Math.abs(A.face.u.x * B.face.u.y - A.face.u.y * B.face.u.x) < 0.1) continue;
        const kb = ([0, 1] as const).find((k) => !B.done[k] && dist(footOf(B, k), C) < 2);
        if (kb == null) continue;
        A.done[ka] = B.done[kb] = true;
        const za = { z0: A.boards[0].z0, z1: A.boards[A.boards.length - 1].z1 }, zb = { z0: B.boards[0].z0, z1: B.boards[B.boards.length - 1].z1 };
        if (Math.min(za.z1, zb.z1) - Math.max(za.z0, zb.z0) < 1) {
          // At different heights they pass: each runs to the other's face.
          A.ends[ka] = { s: A.ends[ka].s, cut: lineAt(B, 0) };
          B.ends[kb] = { s: B.ends[kb].s, cut: lineAt(A, 0) };
          continue;
        }
        const [W, L] = winner(A, B, C);
        const kw = W === A ? ka : kb, kl = W === A ? kb : ka;
        const toward = kw ? W.face.u : { x: -W.face.u.x, y: -W.face.u.y };
        const inside = dot(toward, L.face.n) < 0;
        W.ends[kw] = { s: W.ends[kw].s, cut: lineAt(L, inside ? 0 : L.f.standOff + L.f.boardThickness) };
        L.ends[kl] = { s: L.ends[kl].s, cut: lineAt(W, inside ? W.f.standOff + W.f.boardThickness : W.f.standOff) };
      }
    }
  }
  // A lone end at a sharp corner is cut along the wall it runs into.
  for (const P of placed) {
    if (P.wall == null) continue;
    for (const k of [0, 1] as const) {
      if (P.done[k] || !atEnd(P, k)) continue;
      const adj = edges[k ? (P.wall + 1) % n : (P.wall - 1 + n) % n];
      const toward = k ? P.face.u : { x: -P.face.u.x, y: -P.face.u.y };
      if (adj && dot(toward, adj.normal) < 0) P.ends[k] = { s: P.ends[k].s, cut: { p: adj.ia, dir: adj.dir } };
    }
  }
  for (const P of placed) {
    const so = P.f.standOff;
    const a = Math.max(endStation(P.face, P.ends[0], so), P.flat[0]), b = Math.min(endStation(P.face, P.ends[1], so), P.flat[1]);
    out.runs.set(P.br.id, { face: P.face, s0: P.s0, s1: P.s1, ends: P.ends, spacer: [a, b], auto: P.auto, ...(P.bench?.id ? { bench: P.bench.id } : {}) });
  }
  return out;
}

/** Where a backrest runs along its face (its corners resolved with the room's
 *  other backrests), or null when its host is gone or it has no length left. */
export function backrestRun(inst: ProductInstance, br: Backrest): BackrestRun | null {
  return backrestLayout(inst, br).runs.get(br.id) ?? null;
}

/** The rounded corner pieces of a room's backrests. */
export function backrestCorners(inst: ProductInstance): BackrestCorner[] {
  return backrestLayout(inst).corners;
}

/** Where a backrest goes when one of its panel's figures is typed (stations along
 *  its face): `left` / `right`, its distance from that end of the face, moves that
 *  end alone — the other stays, so a backrest spanning its whole wall is brought in
 *  from either end, and both distances can be typed; `width` keeps its left end
 *  (moving it left where the face runs out). Never under `BACKREST_MIN_LEN`. Null:
 *  it takes its own span again (`auto`, the whole face where none is given) — no
 *  stored ends. */
export function backrestSpanFor(run: { face: { lo: number; hi: number }; s0: number; s1: number; auto?: [number, number] }, edit: { width?: number; left?: number; right?: number }): { start: number; end: number } | null {
  const { lo, hi } = run.face, min = Math.min(BACKREST_MIN_LEN, hi - lo);
  const clamp = (v: number, a: number, b: number): number => Math.min(b, Math.max(a, v));
  let a = run.s0, b = run.s1;
  if (edit.width != null) {
    const w = clamp(edit.width, min, hi - lo);
    a = Math.min(a, hi - w); b = a + w;
  } else if (edit.left != null) a = clamp(lo + edit.left, lo, b - min);
  else if (edit.right != null) b = clamp(hi - edit.right, a + min, hi);
  const [x, y] = run.auto ?? [lo, hi];
  return Math.abs(a - x) <= 0.5 && Math.abs(b - y) <= 0.5 ? null : { start: a, end: b };
}

/** The stations a backrest's end grips stop at along its face: its ends, the
 *  ends of the benches against it and the faces of the partitions on its wall. */
export function backrestStops(inst: ProductInstance, br: Backrest): number[] {
  const run = backrestRun(inst, br);
  if (!run) return [];
  const { lo, hi } = run.face;
  const out = [lo, hi, ...faceBenches(inst, br.host, run.face).flatMap((b) => [b.s0, b.s1])];
  const view = wallView(inst), w = 'partition' in br.host ? null : hostWall(view, br.host);
  for (const iw of view.innerWalls ?? []) {
    const sp = w != null && iw.edge === w ? innerWallSpan(view, iw) : null;
    if (sp) out.push(sp.s, sp.t);
  }
  return [...new Set(out.filter((s) => s >= lo - 0.5 && s <= hi + 0.5).map((s) => Math.round(s * 10) / 10))].sort((a, b) => a - b);
}

/** The boards' plan footprint along its face (room local), its ends cut, or null
 *  when the backrest does not resolve. */
export function backrestBand(inst: ProductInstance, br: Backrest): Vec2[] | null {
  const run = backrestRun(inst, br);
  if (!run) return null;
  const f = backrestFigures(br);
  const q = runQuad(run, f.standOff, f.standOff + f.boardThickness);
  return dist(q[0], q[1]) > 0.5 || dist(q[2], q[3]) > 0.5 ? q : null;
}

/** Where the parts standing in front of a backrest interrupt it
 *  (`ProductInstance.backrestCuts`: an infrared emitter in its own backrest):
 *  the stretches of its face, stations from–to in order and merged, each cut
 *  whose footprint reaches its boards and whose height meets them. */
export function backrestGaps(inst: ProductInstance, br: Backrest, run: BackrestRun): Array<[number, number]> {
  const cuts = inst.backrestCuts;
  if (!cuts?.length) return [];
  const f = backrestFigures(br);
  const boards = backrestBoards(inst, br);
  const z0 = boards[0].z0, z1 = boards[boards.length - 1].z1, deep = f.standOff + f.boardThickness;
  const lo = Math.min(endStation(run.face, run.ends[0], 0), endStation(run.face, run.ends[0], deep));
  const hi = Math.max(endStation(run.face, run.ends[1], 0), endStation(run.face, run.ends[1], deep));
  const spans: Array<[number, number]> = [];
  for (const c of cuts) {
    if (c.poly.length < 3 || c.z1 <= z0 || c.z0 >= z1) continue;
    const rel = c.poly.map((p) => ({ s: dot(sub(p, run.face.o), run.face.u), d: dot(sub(p, run.face.o), run.face.n) }));
    if (Math.max(...rel.map((r) => r.d)) <= 0 || Math.min(...rel.map((r) => r.d)) >= deep) continue;
    const a = Math.max(lo, Math.min(...rel.map((r) => r.s))), b = Math.min(hi, Math.max(...rel.map((r) => r.s)));
    if (b - a > 0.5) spans.push([a, b]);
  }
  spans.sort((x, y) => x[0] - y[0]);
  const out: Array<[number, number]> = [];
  for (const sp of spans) {
    const last = out[out.length - 1];
    if (last && sp[0] <= last[1] + 0.5) last[1] = Math.max(last[1], sp[1]);
    else out.push([sp[0], sp[1]]);
  }
  return out;
}

/** A run's plan quads between depths `d0` and `d1`, its ends cut and its gaps
 *  (`backrestGaps`) left out: square to the face at a gap. */
export function runPieces(run: BackrestRun, d0: number, d1: number, gaps: ReadonlyArray<[number, number]>): Vec2[][] {
  if (!gaps.length) return [runQuad(run, d0, d1)];
  const { face, ends } = run;
  const out: Vec2[][] = [];
  let from: number | null = null; // the square start of the piece under way (null: the run's own end)
  const piece = (to: number | null): void => {
    const s = (d: number): number => (from == null ? endStation(face, ends[0], d) : from);
    const t = (d: number): number => (to == null ? endStation(face, ends[1], d) : to);
    if (Math.min(t(d0) - s(d0), t(d1) - s(d1)) > 0.5) out.push([facePoint(face, s(d0), d0), facePoint(face, t(d0), d0), facePoint(face, t(d1), d1), facePoint(face, s(d1), d1)]);
  };
  for (const [a, b] of gaps) { piece(a); from = b; }
  piece(null);
  return out;
}

/** Every plan piece of a backrest's boards: along its face (its gaps left out)
 *  and round the rounded corners it owns — what the plan fills, the hit test
 *  takes and the RCP sees. */
export function backrestPlan(inst: ProductInstance, br: Backrest, layout = backrestLayout(inst, br)): Vec2[][] {
  const run = layout.runs.get(br.id);
  if (!run) return [];
  const f = backrestFigures(br);
  const along = runPieces(run, f.standOff, f.standOff + f.boardThickness, backrestGaps(inst, br, run))
    .filter((q) => dist(q[0], q[1]) > 0.5 || dist(q[2], q[3]) > 0.5);
  const own = layout.corners.filter((c) => c.owner === br.id).map((c) => c.boards);
  return [...along, ...own];
}

/** Where the spacers stand along the run: one at each end and as many between
 *  as keep them within {@link BACKREST_SPACER_GAP}. */
export function backrestSpacerStations(s0: number, s1: number): number[] {
  const h = BACKREST_SPACER / 2;
  const a = s0 + h, b = s1 - h;
  if (b - a < 1) return [(s0 + s1) / 2];
  const n = Math.ceil((b - a) / BACKREST_SPACER_GAP);
  return Array.from({ length: n + 1 }, (_, k) => a + ((b - a) * k) / n);
}

/** One solid piece of a backrest: a plan footprint and its height band. */
export interface BackrestSolid { part: 'board' | 'spacer'; poly: Vec2[]; base: number; top: number }

/** Every board and spacer of a backrest (room local), the corner pieces it owns
 *  included. The spacers stand behind the boards, from the wall to their back
 *  face, over `backrestSpacerBand`. */
export function backrestSolids(inst: ProductInstance, br: Backrest): BackrestSolid[] {
  const layout = backrestLayout(inst, br);
  const run = layout.runs.get(br.id);
  if (!run) return [];
  const f = backrestFigures(br);
  const boards = backrestBoards(inst, br);
  const out: BackrestSolid[] = [];
  for (const poly of backrestPlan(inst, br, layout)) for (const b of boards) out.push({ part: 'board', poly, base: b.z0, top: b.z1 });
  if (f.standOff > 0.5) {
    const [z0, z1] = backrestSpacerBand(boards[0].z0, boards[boards.length - 1].z1), h = BACKREST_SPACER / 2;
    // Each stretch between its gaps carries its own spacers, one at each end.
    let [a] = run.spacer;
    const stretches: Array<[number, number]> = [];
    for (const [g0, g1] of backrestGaps(inst, br, run)) { stretches.push([a, Math.min(run.spacer[1], g0)]); a = Math.max(a, g1); }
    stretches.push([a, run.spacer[1]]);
    for (const [s0, s1] of stretches) {
      if (s1 - s0 <= 1) continue;
      for (const s of backrestSpacerStations(s0, s1)) {
        out.push({ part: 'spacer', poly: faceQuad(run.face, Math.max(s0, s - h), Math.min(s1, s + h), 0, f.standOff), base: z0, top: z1 });
      }
    }
    for (const c of layout.corners) if (c.owner === br.id) for (const poly of c.spacers) out.push({ part: 'spacer', poly, base: z0, top: z1 });
  }
  return out;
}

/** The backrest under `p` (room local): inside its boards, or within `tol` mm of
 *  them; the nearest wins. */
export function backrestAt(inst: ProductInstance, p: Vec2, tol: number): Backrest | null {
  let best: Backrest | null = null, bestD = Infinity;
  const layout = backrestLayout(inst);
  for (const br of backrestsOf(inst)) {
    for (const poly of backrestPlan(inst, br, layout)) {
      const d = pointInPolygon(p, poly) ? 0 : distToRing(p, poly);
      if (d <= tol && d < bestD) { bestD = d; best = br; }
    }
  }
  return best;
}

function distToRing(p: Vec2, ring: Vec2[]): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const L2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / L2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + (b.x - a.x) * t), p.y - (a.y + (b.y - a.y) * t)));
  }
  return best;
}

/** Every host of a room a backrest can stand on: each wall (a heater wall's
 *  face where one stands) and both faces of every partition. */
export function backrestHosts(inst: ProductInstance): BackrestHost[] {
  if (!isPolyRoom(inst)) return [];
  const view = wallView(inst);
  const out: BackrestHost[] = [];
  polyBenchEdges(view).forEach((_e, w) => {
    const hw = view.heaterWalls?.find((h) => h.edge === w);
    out.push(hw ? { heaterWall: hw.id } : { wall: w });
  });
  for (const iw of view.innerWalls ?? []) {
    if (!iw.id || iw.edge == null) continue;
    out.push({ partition: iw.id, face: 'a' }, { partition: iw.id, face: 'b' });
  }
  return out;
}

/** The host whose face lies under `p` (room local): within `reach` mm in front
 *  of the face, over its stretch, the nearest face first. */
export function backrestHostAt(inst: ProductInstance, p: Vec2, reach = BACKREST_REACH): BackrestHost | null {
  let best: BackrestHost | null = null, bestD = Infinity;
  for (const host of backrestHosts(inst)) {
    const face = backrestFace(inst, host);
    if (!face) continue;
    const s = (p.x - face.o.x) * face.u.x + (p.y - face.o.y) * face.u.y;
    const d = (p.x - face.o.x) * face.n.x + (p.y - face.o.y) * face.n.y;
    if (s < face.lo || s > face.hi || d < -1 || d > reach) continue;
    if (d < bestD) { bestD = d; best = host; }
  }
  return best;
}

/** Whether two hosts are the same face. */
export function sameHost(a: BackrestHost, b: BackrestHost): boolean {
  if ('wall' in a) return 'wall' in b && a.wall === b.wall;
  if ('heaterWall' in a) return 'heaterWall' in b && a.heaterWall === b.heaterWall;
  return 'partition' in b && a.partition === b.partition && a.face === b.face;
}

/** The backrests whose host no longer resolves — a wall gone, a heater wall or
 *  a partition deleted —, which go with it. */
export function orphanBackrests(inst: ProductInstance): string[] {
  return backrestsOf(inst).filter((br) => !backrestFace(inst, br.host)).map((br) => br.id);
}

/** A room's backrests turned into plain pieces for the views that build solids
 *  (section, 3D): each with its id and colour. */
export function roomBackrestSolids(inst: ProductInstance): Array<BackrestSolid & { id: string; color?: string }> {
  return backrestsOf(inst).flatMap((br) => backrestSolids(inst, br).map((s) => ({ ...s, id: br.id, ...(br.color ? { color: br.color } : {}) })));
}

/** A backrest's colour: its own, else the room's bench colour, else the high
 *  tier's — the plan, the section and 3D read it. */
export function backrestColor(inst: ProductInstance, br: Backrest): string {
  return br.color ?? inst.benchColor ?? (inst.category === 'steamroom' ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS).high;
}

/** What may be wrong where a backrest stands: across a door, in front of a pane
 *  or an opening, through a partition standing on its wall, within
 *  {@link BACKREST_HEATER_CLEAR} of a heater (`heaters`: their plan footprints,
 *  room local). */
export type BackrestWarning = 'door' | 'heater' | 'pane' | 'partition';

/** How close to a heater a backrest's boards may come (mm, in plan). */
export const BACKREST_HEATER_CLEAR = 100;

export function backrestWarnings(inst: ProductInstance, br: Backrest, heaters: Vec2[][] = []): BackrestWarning[] {
  const layout = backrestLayout(inst, br);
  const run = layout.runs.get(br.id);
  if (!run) return [];
  const f = backrestFigures(br);
  const a = Math.min(endStation(run.face, run.ends[0], f.standOff), endStation(run.face, run.ends[0], f.standOff + f.boardThickness));
  const b = Math.max(endStation(run.face, run.ends[1], f.standOff), endStation(run.face, run.ends[1], f.standOff + f.boardThickness));
  const over = (s: number, t: number): boolean => Math.min(b, Math.max(s, t)) - Math.max(a, Math.min(s, t)) > 1;
  const out: BackrestWarning[] = [];
  const view = wallView(inst);
  const host = br.host;
  if ('partition' in host) {
    if (innerWallDoorSpans(view, host.partition).some((d) => over(d.start, d.end))) out.push('door');
    const panes = [...innerWallGlassSpans(view, host.partition), ...(view.openings ?? []).filter((o) => o.innerWallId === host.partition).map((o) => ({ start: o.start, end: o.end }))];
    if (panes.some((g) => over(g.start, g.end))) out.push('pane');
  } else {
    const w = hostWall(view, host);
    if (outerDoorSpans(view).some((d) => d.edge === w && over(d.start, d.end))) out.push('door');
    if (w != null && wallFeatures(inst, w).some((g) => over(g.s, g.t))) out.push('pane');
    if ((view.innerWalls ?? []).some((iw) => {
      const sp = iw.edge === w ? innerWallSpan(view, iw) : null;
      return !!sp && over(sp.s + 1, sp.t - 1);
    })) out.push('partition');
  }
  const plan = backrestPlan(inst, br, layout);
  if (heaters.some((h) => plan.some((p) => polygonGap(p, h) < BACKREST_HEATER_CLEAR))) out.push('heater');
  return out;
}

/** How far apart two polygons are in plan (0 where they touch or overlap). */
function polygonGap(a: Vec2[], b: Vec2[]): number {
  if (a.some((p) => pointInPolygon(p, b)) || b.some((p) => pointInPolygon(p, a))) return 0;
  const side = (p: Vec2, q: Vec2, r: Vec2): number => Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
  for (let i = 0; i < a.length; i++) {
    const p = a[i], q = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const r = b[j], s = b[(j + 1) % b.length];
      if (side(p, q, r) !== side(p, q, s) && side(r, s, p) !== side(r, s, q)) return 0;
    }
  }
  let best = Infinity;
  for (const p of a) best = Math.min(best, distToRing(p, b));
  for (const p of b) best = Math.min(best, distToRing(p, a));
  return best;
}

/** The stretches of a path (room local) that no cut at height `z` covers, in
 *  order: the path split where it runs through a cut's footprint. The whole path
 *  when none does. */
export function pathOutsideCuts(pts: readonly Vec2[], z: number, cuts: readonly BackrestCut[] | undefined): Vec2[][] {
  const live = (cuts ?? []).filter((c) => c.poly.length >= 3 && z >= c.z0 && z <= c.z1);
  if (!live.length || pts.length < 2) return [pts.slice()];
  const out: Vec2[][] = [];
  let cur: Vec2[] = [];
  const inside = (p: Vec2): boolean => live.some((c) => pointInPolygon(p, c.poly));
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k], b = pts[k + 1];
    // Where the segment crosses any cut's outline, in order along it.
    const ts = [0, 1];
    for (const c of live) {
      for (let i = 0; i < c.poly.length; i++) {
        const t = segmentCross(a, b, c.poly[i], c.poly[(i + 1) % c.poly.length]);
        if (t != null) ts.push(t);
      }
    }
    ts.sort((x, y) => x - y);
    const at = (t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    for (let i = 0; i + 1 < ts.length; i++) {
      const t0 = ts[i], t1 = ts[i + 1];
      if (t1 - t0 < 1e-9) continue;
      if (inside(at((t0 + t1) / 2))) {
        if (cur.length >= 2) out.push(cur);
        cur = [];
        continue;
      }
      const p0 = at(t0), p1 = at(t1);
      const last = cur[cur.length - 1];
      if (!last || Math.hypot(last.x - p0.x, last.y - p0.y) > 1e-6) {
        if (cur.length >= 2) out.push(cur);
        cur = [p0];
      }
      cur.push(p1);
    }
  }
  if (cur.length >= 2) out.push(cur);
  return out;
}

/** Where segment a–b crosses segment c–d, as a fraction along a–b; null where
 *  it does not (or they are parallel). */
function segmentCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): number | null {
  const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / den;
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / den;
  return t > 0 && t < 1 && u >= 0 && u <= 1 ? t : null;
}
